import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import http from '../api/client';
import { showSuccessToast } from '../utils/toast';
import {
  profileCompletionDetailsSchema,
  profileCompletionProfileSchema,
} from '../auth/schemas/profileCompletion.schema';
import { emailOtpSchema } from '../auth/schemas/signup.schema';
import {
  zodErrorsToFieldErrors,
  apiValidationToFieldErrors,
} from '../auth/schemas/utils';
import { errorHandler } from '../api/errorHandler';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Mail,
  Lock,
  CheckCircle,
  Calendar,
  User,
  Globe,
  Languages,
  MapPin,
  ArrowRight,
  LogOut,
} from 'lucide-react';
import { ButtonLoader } from './common/ButtonLoader';
import FieldInfoTooltip from './common/FieldInfoTooltip';
import AvatarCropDialog from './common/AvatarCropDialog';
import AvatarPhotoPickerDialog from './common/AvatarPhotoPickerDialog';
import CameraCaptureDialog from './common/CameraCaptureDialog';
import LocationAutocomplete from './common/LocationAutocomplete';
import { getStatesForCountry } from '../data/location';

const ProfileCompletionGate = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, logout, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const skipModalRef = useRef(false);
  const [step, setStep] = useState<'details' | 'otp' | 'profile'>(() => {
    // Derive the correct initial step from user data that's already in the
    // auth context so the modal never renders the wrong step on first paint.
    if (!user) return 'details';
    const rawV = user?.verification_email as unknown;
    const verified = rawV === true || rawV === 1 || rawV === 'true';
    return verified ? 'profile' : 'details';
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirm_password: '',
    otp: '',
    dob: '',
    gender: '',
    country: '',
    state: '',
    city: '',
    street1: '',
    street2: '',
    preferred_language: 'en',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [showPickerDialog, setShowPickerDialog] = useState(false);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [hasConsumedSkipFlag] = useState(() => {
    // One‑time escape hatch to prevent a brief popup flash right after a
    // full doctor registration flow completes and redirects into the app.
    try {
      if (typeof window === 'undefined') return false;
      const shouldSkipOnce =
        window.sessionStorage.getItem('skip_profile_completion_gate_once') ===
        'true';

      if (shouldSkipOnce) {
        window.sessionStorage.removeItem('skip_profile_completion_gate_once');
        return true;
      }
    } catch {
      // If sessionStorage is not available for any reason, just fall back to normal behaviour.
    }

    return false;
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<string, string>>
  >({});
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpirySeconds, setOtpExpirySeconds] = useState(600); // 10 minutes

  // Normalize verification + profile flags once so both effects and render path
  // use the exact same logic.
  const rawVerification = user?.verification_email as unknown;
  const isVerified =
    rawVerification === true ||
    rawVerification === 1 ||
    rawVerification === 'true';

  const isProfileComplete = (user?.profile_completion_level || 0) >= 3;

  // Initialize form only once when user data is available
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (isLoading) return;
    if (isInitialized) return;

    setForm((prev) => ({
      ...prev,
      email: user.email || '',
      dob: user.dob || '',
      gender: user.gender || '',
      country: user.country || '',
      state: user.state || '',
      city: user.city || '',
      street1: ((user as Record<string, unknown>).street1 as string) || '',
      street2: ((user as Record<string, unknown>).street2 as string) || '',
      preferred_language: user.preferred_language || 'en',
    }));
    setPreviewImage(user.avatar || null);
    setIsInitialized(true);
  }, [isAuthenticated, isLoading, user, isInitialized]);

  // Handle modal step based on verification status (details vs profile).
  // Depends on isVerified + isProfileComplete directly so that a user-state
  // change (e.g. after refreshUser) always picks the correct step even when
  // the user object reference hasn't changed.
  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated && user) {
      if (user.role === 'admin') return;

      if (!isVerified) {
        setStep('details');
      } else if (!isProfileComplete) {
        setStep('profile');
      }
    } else {
      setIsInitialized(false);
    }
  }, [isAuthenticated, isLoading, user, isVerified, isProfileComplete]);

  // OTP expiry countdown timer (must be before early returns to follow rules of hooks)
  useEffect(() => {
    if (step !== 'otp' || otpExpirySeconds <= 0) return;
    const t = setInterval(() => setOtpExpirySeconds((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [step, otpExpirySeconds]);

  // During any auth/user refresh, do not render the gate at all to avoid flashes.
  if (isLoading) return null;
  if (!user) return null;

  const justSubmitted = (() => {
    try {
      const v = sessionStorage.getItem('profile_completion_just_submitted');
      if (v === 'true') {
        sessionStorage.removeItem('profile_completion_just_submitted');
        return true;
      }
    } catch {
      /* ignore */
    }
    return false;
  })();

  const shouldShowModal =
    !justSubmitted &&
    isAuthenticated &&
    user.role !== 'admin' &&
    !hasConsumedSkipFlag &&
    !skipModalRef.current &&
    (!isVerified || !isProfileComplete);

  // If onboarding is not required for the current user state, render nothing.
  if (!shouldShowModal) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'otp') setOtpError(null);
  };

  const handleResendOTP = async () => {
    if (loading || resendCooldown > 0 || !user?.id) return;
    setLoading(true);
    setOtpError(null);
    try {
      await http.post('/auth/resend-otp', { user_id: user.id });
      showSuccessToast('New OTP sent to your email');
      setResendCooldown(60);
      setOtpExpirySeconds(600); // Reset expiry timer on resend
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: unknown) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      const validationErrors = apiValidationToFieldErrors(err.response?.data);
      setOtpError(
        validationErrors?.otp ?? errorHandler(error) ?? 'Failed to resend OTP',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToChangeEmail = () => {
    if (loading) return;
    setStep('details');
    setOtpError(null);
    setOtpExpirySeconds(600);
  };

  const handleFileOrCaptureChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFieldErrors((prev) => ({
        ...prev,
        avatar: 'Please choose an image file',
      }));
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setFieldErrors((prev) => ({
        ...prev,
        avatar: 'Image should be less than 20MB',
      }));
      return;
    }
    setFieldErrors((prev) => ({ ...prev, avatar: undefined }));
    setCropImageUrl(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleCropComplete = (blob: Blob) => {
    if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
    setCropImageUrl(null);
    const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
    setAvatarFile(file);
    setPreviewImage(URL.createObjectURL(blob));
  };

  const handleCropCancel = () => {
    if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
    setCropImageUrl(null);
  };

  const openGalleryPicker = () => {
    fileInputRef.current?.click();
  };

  const handleCameraCapture = (blob: Blob) => {
    setShowCameraDialog(false);
    setCropImageUrl(URL.createObjectURL(blob));
  };

  const handleSetCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    const result = profileCompletionDetailsSchema.safeParse({
      email: form.email,
      password: form.password,
      confirm_password: form.confirm_password,
    });
    if (!result.success) {
      setFieldErrors(zodErrorsToFieldErrors(result.error));
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      await http.post('/auth/signup-credentials', {
        user_id: user?.id,
        email: form.email,
        password: form.password,
        confirm_password: form.confirm_password,
      });

      setStep('otp');
      setOtpExpirySeconds(600); // Reset timer on new OTP
      showSuccessToast('OTP sent to your email');
    } catch (error: unknown) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      const validationErrors = apiValidationToFieldErrors(err.response?.data);
      if (validationErrors) {
        setFieldErrors(validationErrors);
      } else {
        setFieldErrors({
          email: errorHandler(error) || 'Failed to update credentials',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = emailOtpSchema.safeParse({ otp: form.otp });
    if (!result.success) {
      setOtpError(
        zodErrorsToFieldErrors(result.error).otp ?? 'Please enter OTP',
      );
      return;
    }

    setLoading(true);
    setOtpError(null);
    try {
      const res = await http.post('/auth/signup-verify-email', {
        user_id: user?.id,
        otp: form.otp,
      });

      // Update local storage and user state
      localStorage.setItem('token', res.data.accessToken);
      localStorage.setItem('refreshToken', res.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(res.data.user));

      if ((res.data.user.profile_completion_level || 0) < 3) {
        setStep('profile');
        showSuccessToast('Email verified! Please complete your profile.');
      } else {
        showSuccessToast('Signup successful! Your profile is complete.');
        await refreshUser();
        const u = res.data.user;
        const profilePath =
          u?.active_profile_type === 'patient'
            ? '/profile'
            : u?.role === 'doctor'
              ? '/doctor/profile'
              : '/profile';
        navigate(profilePath);
      }
    } catch (error: unknown) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      const validationErrors = apiValidationToFieldErrors(err.response?.data);
      const errMsg =
        validationErrors?.otp ?? errorHandler(error) ?? 'Invalid OTP';
      setOtpError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const avatarError =
      !avatarFile && !previewImage
        ? { avatar: 'Please upload a profile photo' }
        : {};

    const result = profileCompletionProfileSchema.safeParse({
      dob: form.dob,
      gender: form.gender,
      country: form.country,
      state: form.state,
      city: form.city,
      street1: form.street1,
      street2: form.street2,
      preferred_language: form.preferred_language,
    });

    if (!result.success || Object.keys(avatarError).length > 0) {
      const zodFieldErrors = result.success
        ? {}
        : zodErrorsToFieldErrors(result.error);
      setFieldErrors({
        ...zodFieldErrors,
        ...avatarError,
      });
      return;
    }

    setFieldErrors({});

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('user_id', String(user?.id));
      formData.append('dob', form.dob);
      formData.append('gender', form.gender);
      formData.append('country', form.country);
      formData.append('state', form.state);
      formData.append('city', form.city);
      formData.append('street1', form.street1 || '');
      formData.append('street2', form.street2 || '');
      formData.append('preferred_language', form.preferred_language);
      formData.append('email', form.email);

      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      const res = await http.post('/auth/signup-profile', formData);

      // Suppress the modal before refreshUser triggers a re-render
      skipModalRef.current = true;
      try {
        sessionStorage.setItem('profile_completion_just_submitted', 'true');
      } catch {
        /* ignore */
      }

      // Refresh auth context FIRST so the profile page renders with the new
      // avatar immediately — navigating before this would show the old image.
      localStorage.setItem('user', JSON.stringify(res.data.user));
      await refreshUser();

      showSuccessToast('Signup successful! Your profile is complete.');

      const u = res.data.user;
      const profilePath =
        u?.active_profile_type === 'patient'
          ? '/profile'
          : u?.role === 'doctor'
            ? '/doctor/profile'
            : '/profile';
      navigate(profilePath, { replace: true });
    } catch (error: unknown) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      const validationErrors = apiValidationToFieldErrors(err.response?.data);
      if (validationErrors) {
        setFieldErrors(validationErrors);
      } else {
        setFieldErrors({
          dob: errorHandler(error) || 'Failed to update profile',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (logoutLoading) return;
    setLogoutLoading(true);
    try {
      await logout();
    } finally {
      setLogoutLoading(false);
    }
  };

  return (
    <div>
      <style>{`
        @keyframes profileGateBackdropFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes profileGateCardSlideUp {
          0% {
            opacity: 0;
            transform: translateY(16px) scale(0.98);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .profile-gate-backdrop-enter {
          animation: profileGateBackdropFadeIn 220ms ease-out;
        }
        .profile-gate-card-enter {
          animation: profileGateCardSlideUp 220ms ease-out;
        }
      `}</style>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#2d2416]/60 backdrop-blur-xl px-4 py-8 overflow-y-auto profile-gate-backdrop-enter">
        <div className="bg-white rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.3)] w-full max-w-lg overflow-hidden my-auto border border-white/20 profile-gate-card-enter">
          {/* Header */}
          <div className="bg-herbal-green p-8 text-center text-white relative overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />

            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/30 backdrop-blur-sm">
                {step === 'details' && <Lock className="w-8 h-8 text-white" />}
                {step === 'otp' && <Mail className="w-8 h-8 text-white" />}
                {step === 'profile' && <User className="w-8 h-8 text-white" />}
              </div>

              <div className="flex flex-col gap-1">
                <h2 className="text-2xl font-bold">
                  {step === 'details' && 'Security Setup'}
                  {step === 'otp' && 'Verify Email'}
                  {step === 'profile' && 'About You'}
                </h2>
                <p className="text-white/80 text-sm leading-relaxed max-w-[280px] mx-auto">
                  {step === 'details' &&
                    'Secure your account with a password and email.'}
                  {step === 'otp' &&
                    "We've sent a code to keep your account safe."}
                  {step === 'profile' &&
                    'Tell us a bit more to personalize your experience.'}
                </p>
              </div>

              {/* Step Indicator */}
              <div className="flex justify-center gap-2 mt-6">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${step === 'details' ? 'w-8 bg-white' : 'w-2 bg-white/30'}`}
                />
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${step === 'otp' ? 'w-8 bg-white' : 'w-2 bg-white/30'}`}
                />
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${step === 'profile' ? 'w-8 bg-white' : 'w-2 bg-white/30'}`}
                />
              </div>
            </div>
          </div>

          <div className="p-8">
            {step === 'details' ? (
              <form
                onSubmit={handleSetCredentials}
                className="space-y-5"
                noValidate
              >
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-herbal-green" /> Email Address
                    <span className="text-red-500">*</span>
                    <FieldInfoTooltip content="For account recovery and personalized communication. We use your email to send remedy recommendations and important updates." />
                  </label>
                  <input
                    name="email"
                    type="email"
                    className={`w-full rounded-lg border-2 px-4 py-3.5 focus:outline-none transition-colors text-[#2d2416] bg-white placeholder-[#9e9890] ${
                      fieldErrors.email
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                    }`}
                    placeholder="you@example.com"
                    value={form.email.startsWith('temp_') ? '' : form.email}
                    onChange={(e) => {
                      handleChange(e);
                      if (fieldErrors.email)
                        setFieldErrors((prev) => ({
                          ...prev,
                          email: undefined,
                        }));
                    }}
                  />
                  {fieldErrors.email && (
                    <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-herbal-green" /> Password
                    <span className="text-red-500">*</span>
                    <FieldInfoTooltip content="Keeps your account secure. We never store your password in plain text." />
                  </label>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      minLength={6}
                      className={`w-full rounded-lg border-2 px-4 py-3.5 pr-20 focus:outline-none transition-colors text-[#2d2416] bg-white placeholder-[#9e9890] ${
                        fieldErrors.password
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                      }`}
                      placeholder="Create a strong password"
                      value={form.password}
                      onChange={(e) => {
                        handleChange(e);
                        if (fieldErrors.password)
                          setFieldErrors((prev) => ({
                            ...prev,
                            password: undefined,
                          }));
                      }}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-3 z-10 flex items-center gap-1.5 text-sm font-medium text-herbal-green hover:text-herbal-green-dark transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showPassword ? (
                        <>
                          <EyeOff size={18} />
                          <span>Hide</span>
                        </>
                      ) : (
                        <>
                          <Eye size={18} />
                          <span>Show</span>
                        </>
                      )}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      {fieldErrors.password}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-herbal-green" /> Confirm
                    Password
                    <span className="text-red-500">*</span>
                    <FieldInfoTooltip content="Ensures you typed your password correctly." />
                  </label>
                  <div className="relative">
                    <input
                      name="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`w-full rounded-lg border-2 px-4 py-3.5 pr-20 focus:outline-none transition-colors text-[#2d2416] bg-white placeholder-[#9e9890] ${
                        fieldErrors.confirm_password
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                      }`}
                      placeholder="Repeat your password"
                      value={form.confirm_password}
                      onChange={(e) => {
                        handleChange(e);
                        if (fieldErrors.confirm_password)
                          setFieldErrors((prev) => ({
                            ...prev,
                            confirm_password: undefined,
                          }));
                      }}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-3 z-10 flex items-center gap-1.5 text-sm font-medium text-herbal-green hover:text-herbal-green-dark transition-colors"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      aria-label={
                        showConfirmPassword
                          ? 'Hide confirm password'
                          : 'Show confirm password'
                      }
                    >
                      {showConfirmPassword ? (
                        <>
                          <EyeOff size={18} />
                          <span>Hide</span>
                        </>
                      ) : (
                        <>
                          <Eye size={18} />
                          <span>Show</span>
                        </>
                      )}
                    </button>
                  </div>
                  {fieldErrors.confirm_password && (
                    <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      {fieldErrors.confirm_password}
                    </p>
                  )}
                </div>

                <div className="pt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={logoutLoading}
                    className="flex-1 py-4 px-6 rounded-lg border-2 border-[#e0ddd5] text-[#6b6358] font-bold hover:bg-slate-50 hover:text-slate-700 transition-all font-body text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {logoutLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <ButtonLoader size={18} className="text-[#6b6358]" />
                        Logging out...
                      </span>
                    ) : (
                      'Log out'
                    )}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-[2] bg-[#7E8D4C] hover:bg-[#6f7d42] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <ButtonLoader size={20} className="text-white" />
                    ) : (
                      <ArrowRight className="w-5 h-5" />
                    )}
                    {loading ? 'Sending Code...' : 'Continue'}
                  </button>
                </div>
              </form>
            ) : step === 'otp' ? (
              <form
                onSubmit={handleVerifyEmail}
                className="space-y-6"
                noValidate
              >
                {/* Email confirmation card */}
                <div className="rounded-2xl bg-[#7E8D4C]/5 border border-[#7E8D4C]/20 p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[#7E8D4C]/15 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#7E8D4C]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 mb-0.5">
                        Verification code sent
                      </p>
                      <p className="text-sm text-slate-600 mb-2">
                        We sent a 6-digit code to{' '}
                        <span className="font-semibold text-slate-800 break-all">
                          {form.email}
                        </span>
                      </p>
                      <button
                        type="button"
                        onClick={handleBackToChangeEmail}
                        disabled={loading}
                        className="text-sm font-semibold text-[#7E8D4C] hover:text-[#6f7d42] hover:underline transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                      >
                        Use a different email
                      </button>
                    </div>
                  </div>
                </div>

                {/* OTP input section */}
                <div>
                  <label className="block text-sm font-bold text-slate-800 mb-2">
                    Enter verification code
                  </label>
                  <input
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className={`w-full px-4 py-4 rounded-lg border-2 focus:outline-none transition-colors text-center tracking-[0.35em] text-2xl font-bold font-mono text-[#2d2416] bg-white ${
                      otpError
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                    }`}
                    value={form.otp}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^[0-9]+$/.test(value)) {
                        setForm((prev) => ({
                          ...prev,
                          otp: value.slice(0, 6),
                        }));
                        setOtpError(null);
                      }
                    }}
                  />
                  {otpError && (
                    <p className="mt-2 text-xs font-medium text-red-600 text-center flex items-center justify-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      {otpError}
                    </p>
                  )}
                  <p className="mt-3 text-xs text-slate-500 text-center">
                    {otpExpirySeconds > 0
                      ? `Code expires in ${Math.floor(otpExpirySeconds / 60)}:${String(otpExpirySeconds % 60).padStart(2, '0')}`
                      : 'Code expired. Please request a new one.'}
                  </p>
                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={loading || resendCooldown > 0}
                      className="text-sm font-semibold text-[#7E8D4C] hover:text-[#6f7d42] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendCooldown > 0
                        ? `Resend code in ${resendCooldown}s`
                        : "Didn't receive it? Resend code"}
                    </button>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-4 pt-2">
                  <button
                    type="button"
                    onClick={handleBackToChangeEmail}
                    disabled={loading}
                    className="flex-1 py-4 px-6 rounded-lg border-2 border-[#e0ddd5] text-[#6b6358] font-bold hover:bg-slate-50 hover:border-slate-300 transition-all font-body text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    Change Email
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    aria-busy={loading}
                    className="flex-[2] bg-[#7E8D4C] hover:bg-[#6f7d42] disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none text-white font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <ButtonLoader size={20} className="text-white" />
                    ) : (
                      <CheckCircle className="w-5 h-5" />
                    )}
                    {loading ? 'Verifying...' : 'Verify Email'}
                  </button>
                </div>
              </form>
            ) : (
              <form
                onSubmit={handleUpdateProfile}
                className="space-y-6"
                noValidate
              >
                <div className="flex flex-col items-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileOrCaptureChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPickerDialog(true)}
                    className="relative w-24 h-24 rounded-full bg-slate-50 border-2 border-dashed border-[#e0ddd5] flex items-center justify-center overflow-hidden transition-all hover:border-[#7E8D4C] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#7E8D4C]"
                  >
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    )}
                  </button>
                  <AvatarPhotoPickerDialog
                    isOpen={showPickerDialog}
                    onChooseFromDevice={openGalleryPicker}
                    onTakePicture={() => setShowCameraDialog(true)}
                    onCancel={() => setShowPickerDialog(false)}
                  />
                  <CameraCaptureDialog
                    isOpen={showCameraDialog}
                    onCapture={handleCameraCapture}
                    onCancel={() => setShowCameraDialog(false)}
                  />
                  <AvatarCropDialog
                    isOpen={!!cropImageUrl}
                    imageUrl={cropImageUrl || ''}
                    onComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                  />
                  {fieldErrors.avatar && (
                    <p className="text-xs font-medium text-red-600 mt-2 flex items-center justify-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      {fieldErrors.avatar}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[13px] font-bold text-slate-600 flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-herbal-green" />{' '}
                      Birthday
                      <span className="text-red-500">*</span>
                      <FieldInfoTooltip content="We personalize remedies based on age. Ayurvedic remedies can vary based on age." />
                    </label>
                    <input
                      name="dob"
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                      className={`w-full px-4 py-3.5 rounded-lg border-2 focus:outline-none transition-colors text-[#2d2416] bg-white text-sm ${
                        fieldErrors.dob
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                      }`}
                      value={form.dob}
                      onChange={(e) => {
                        handleChange(e);
                        if (fieldErrors.dob)
                          setFieldErrors((prev) => ({
                            ...prev,
                            dob: undefined,
                          }));
                      }}
                    />
                    {fieldErrors.dob && (
                      <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        {fieldErrors.dob}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[13px] font-bold text-slate-600 flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-herbal-green" /> Gender
                      <span className="text-red-500">*</span>
                      <FieldInfoTooltip content="We personalize remedies based on gender. Ayurvedic remedies can be gender specific." />
                    </label>
                    <div className="relative">
                      <select
                        name="gender"
                        className={`w-full px-4 py-3.5 rounded-lg border-2 focus:outline-none transition-colors text-[#2d2416] bg-white appearance-none text-sm ${
                          fieldErrors.gender
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                        }`}
                        value={form.gender}
                        onChange={(e) => {
                          handleChange(e);
                          if (fieldErrors.gender)
                            setFieldErrors((prev) => ({
                              ...prev,
                              gender: undefined,
                            }));
                        }}
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                    {fieldErrors.gender && (
                      <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        {fieldErrors.gender}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[13px] font-bold text-slate-600 flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-herbal-green" />{' '}
                      Country
                      <span className="text-red-500">*</span>
                      <FieldInfoTooltip content="We personalize remedies based on geographic location. Ayurvedic remedies can vary based on type of land and regional ingredients." />
                    </label>
                    <LocationAutocomplete
                      type="country"
                      value={form.country}
                      onChange={(v) => {
                        setForm((prev) => ({ ...prev, country: v }));
                        if (fieldErrors.country)
                          setFieldErrors((prev) => ({
                            ...prev,
                            country: undefined,
                          }));
                      }}
                      onCountryChange={() =>
                        setForm((prev) => ({ ...prev, state: '' }))
                      }
                      placeholder="Select or type country"
                      inputClassName={`w-full px-4 py-3.5 rounded-lg border-2 focus:outline-none transition-colors text-[#2d2416] bg-white text-sm ${
                        fieldErrors.country
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                      }`}
                    />
                    {fieldErrors.country && (
                      <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        {fieldErrors.country}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[13px] font-bold text-slate-600 flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-herbal-green" /> State
                      <span className="text-red-500">*</span>
                      <FieldInfoTooltip content="We personalize remedies based on geographic location. Ayurvedic remedies can vary based on type of land and regional ingredients." />
                    </label>
                    <LocationAutocomplete
                      type="state"
                      value={form.state}
                      onChange={(v) => {
                        setForm((prev) => ({ ...prev, state: v }));
                        if (fieldErrors.state)
                          setFieldErrors((prev) => ({
                            ...prev,
                            state: undefined,
                          }));
                      }}
                      country={form.country}
                      placeholder={
                        getStatesForCountry(form.country)
                          ? 'Select or type state'
                          : 'Enter state'
                      }
                      inputClassName={`w-full px-4 py-3.5 rounded-lg border-2 focus:outline-none transition-colors text-[#2d2416] bg-white text-sm ${
                        fieldErrors.state
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                      }`}
                    />
                    {fieldErrors.state && (
                      <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        {fieldErrors.state}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-1.5">
                    <label className="text-[13px] font-bold text-slate-600 flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-herbal-green" />{' '}
                      Street 1 (optional)
                    </label>
                    <input
                      name="street1"
                      type="text"
                      className="w-full px-4 py-3.5 rounded-lg border-2 focus:outline-none transition-colors text-[#2d2416] bg-white placeholder-[#9e9890] text-sm border-[#e0ddd5] focus:border-[#7E8D4C]"
                      placeholder="Address line 1"
                      value={form.street1}
                      onChange={(e) => handleChange(e)}
                    />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <label className="text-[13px] font-bold text-slate-600 flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-herbal-green" />{' '}
                      Street 2 (optional)
                    </label>
                    <input
                      name="street2"
                      type="text"
                      className="w-full px-4 py-3.5 rounded-lg border-2 focus:outline-none transition-colors text-[#2d2416] bg-white placeholder-[#9e9890] text-sm border-[#e0ddd5] focus:border-[#7E8D4C]"
                      placeholder="Address line 2 (optional)"
                      value={form.street2}
                      onChange={(e) => handleChange(e)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[13px] font-bold text-slate-600 flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-herbal-green" /> City
                      <span className="text-red-500">*</span>
                      <FieldInfoTooltip content="We personalize remedies based on geographic location. Ayurvedic remedies can vary based on type of land and regional ingredients." />
                    </label>
                    <input
                      name="city"
                      type="text"
                      className={`w-full px-4 py-3.5 rounded-lg border-2 focus:outline-none transition-colors text-[#2d2416] bg-white text-sm ${
                        fieldErrors.city
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                      }`}
                      placeholder="Ahmedabad"
                      value={form.city}
                      onChange={(e) => {
                        handleChange(e);
                        if (fieldErrors.city)
                          setFieldErrors((prev) => ({
                            ...prev,
                            city: undefined,
                          }));
                      }}
                    />
                    {fieldErrors.city && (
                      <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        {fieldErrors.city}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[13px] font-bold text-slate-600 flex items-center gap-2">
                      <Languages className="w-3.5 h-3.5 text-herbal-green" />{' '}
                      Spoken Language
                      <span className="text-red-500">*</span>
                      <FieldInfoTooltip content="We display remedies and content in your spoken language for a better experience." />
                    </label>
                    <div className="relative">
                      <select
                        name="preferred_language"
                        className={`w-full px-4 py-3.5 rounded-lg border-2 focus:outline-none transition-colors text-[#2d2416] bg-white appearance-none text-sm ${
                          fieldErrors.preferred_language
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-[#e0ddd5] focus:border-[#7E8D4C]'
                        }`}
                        value={form.preferred_language}
                        onChange={(e) => {
                          handleChange(e);
                          if (fieldErrors.preferred_language)
                            setFieldErrors((prev) => ({
                              ...prev,
                              preferred_language: undefined,
                            }));
                        }}
                      >
                        <option value="en">English</option>
                        <option value="hi">Hindi</option>
                        <option value="gu">Gujarati</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  {fieldErrors.preferred_language && (
                    <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      {fieldErrors.preferred_language}
                    </p>
                  )}
                </div>

                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={logoutLoading}
                    className="flex-1 py-4 px-4 rounded-lg border-2 border-[#e0ddd5] text-[#6b6358] font-bold hover:bg-slate-50 transition-all font-body text-xs flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {logoutLoading ? (
                      <>
                        <ButtonLoader size={18} className="text-[#6b6358]" />
                        Logging out...
                      </>
                    ) : (
                      <>
                        <LogOut className="w-4 h-4" />
                        Logout
                      </>
                    )}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-[2.5] bg-[#7E8D4C] hover:bg-[#6f7d42] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-3 group"
                  >
                    {loading ? (
                      <ButtonLoader size={20} className="text-white" />
                    ) : (
                      <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                    )}
                    <span className="text-sm">
                      {loading ? 'Finalizing...' : 'Complete My Profile'}
                    </span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionGate;
